import React, { useState, useEffect } from 'react';
import { Button, Typography, Progress } from 'antd';
import { 
  DeleteOutlined, 
  FileImageOutlined, 
  FileTextOutlined, 
  SoundOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  LoadingOutlined 
} from '@ant-design/icons';
import styled from 'styled-components';
import { FileUploadItem } from '../hooks/useFileUpload';

const { Text } = Typography;

// ChatGPT风格的文件附件容器
const AttachmentContainer = styled.div`
  position: relative;
  margin-bottom: 8px;
  background: var(--bg-secondary, #f7f7f8);
  border-radius: 12px;
  border: 1px solid var(--border-color, #e5e7eb);
  overflow: hidden;
  animation: slideInUp 0.3s ease-out;
  
  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const AttachmentList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--border-color, #e5e7eb);
    border-radius: 2px;
  }
`;

const AttachmentItem = styled.div<{ $status: FileUploadItem['status'] }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--border-color-light, #f3f4f6);
  transition: all 0.2s ease;
  position: relative;
  background: ${props => {
    switch (props.$status) {
      case 'success': return 'rgba(34, 197, 94, 0.05)';
      case 'error': return 'rgba(239, 68, 68, 0.05)';
      case 'uploading': return 'rgba(59, 130, 246, 0.05)';
      default: return 'transparent';
    }
  }};
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: var(--bg-hover, #f3f4f6);
  }
`;

const FileIcon = styled.div<{ $type: string; $status: FileUploadItem['status'] }>`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 16px;
  background: ${props => {
    switch (props.$type) {
      case 'image': return 'linear-gradient(135deg, #10b981, #059669)';
      case 'audio': return 'linear-gradient(135deg, #8b5cf6, #7c3aed)';
      case 'document': return 'linear-gradient(135deg, #ef4444, #dc2626)';
      default: return 'linear-gradient(135deg, #6b7280, #4b5563)';
    }
  }};
  color: white;
  position: relative;
  overflow: hidden;
  
  ${props => props.$status === 'uploading' && `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
      animation: shimmer 1.5s infinite;
    }
    
    @keyframes shimmer {
      0% { left: -100%; }
      100% { left: 100%; }
    }
  `}
`;

const ImagePreview = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  border: 2px solid var(--border-color, #e5e7eb);
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FileName = styled(Text)`
  font-weight: 500;
  font-size: 14px;
  color: var(--text-primary, #2d3748);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--text-secondary, #8e8ea0);
`;

const FileTag = styled.span<{ $color?: string }>`
  background: ${props => props.$color || 'var(--bg-hover, #f3f4f6)'};
  color: var(--text-secondary, #8e8ea0);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
`;

const StatusIndicator = styled.div<{ $status: FileUploadItem['status'] }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: ${props => {
    switch (props.$status) {
      case 'success': return '#10b981';
      case 'error': return '#ef4444';
      case 'uploading': return '#3b82f6';
      default: return 'var(--text-secondary, #8e8ea0)';
    }
  }};
`;

const AudioPlayer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  padding: 4px 8px;
  background: var(--bg-hover, #f3f4f6);
  border-radius: 6px;
  font-size: 12px;
`;

const PlayButton = styled(Button)`
  &.ant-btn {
    width: 20px;
    height: 20px;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--primary-color, #10a37f);
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background: rgba(16, 163, 127, 0.1);
      color: var(--primary-color, #10a37f);
    }
  }
`;

const RemoveButton = styled(Button)`
  &.ant-btn {
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--text-secondary, #8e8ea0);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.7;
    transition: all 0.2s ease;
    
    &:hover {
      background: rgba(239, 68, 68, 0.1);
      color: #ef4444;
      opacity: 1;
      transform: scale(1.1);
    }
  }
`;

const ProgressBar = styled.div<{ $progress?: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--border-color, #e5e7eb);
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: var(--primary-color, #10a37f);
    width: ${props => props.$progress || 0}%;
    transition: width 0.3s ease;
  }
`;

// 组件接口
interface ChatFileAttachmentProps {
  files: FileUploadItem[];
  onRemove: (fileId: string) => void;
  compact?: boolean;
}

/**
 * 格式化文件大小
 */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

/**
 * 获取文件图标
 */
const getFileIcon = (type: string, file: FileUploadItem) => {
  if (type === 'image' && file.url) {
    return <ImagePreview src={file.url} alt={file.file.name} />;
  }
  
  const IconComponent = {
    image: FileImageOutlined,
    audio: SoundOutlined,
    document: FileTextOutlined
  }[type] || FileTextOutlined;
  
  return (
    <FileIcon $type={type} $status={file.status}>
      <IconComponent />
    </FileIcon>
  );
};

/**
 * ChatGPT风格的文件附件组件
 */
const ChatFileAttachment: React.FC<ChatFileAttachmentProps> = ({
  files,
  onRemove,
  compact = false
}) => {
  const [audioStates, setAudioStates] = useState<Record<string, { playing: boolean; duration: number }>>({});

  // 获取音频时长
  useEffect(() => {
    files.forEach(file => {
      if (file.type === 'audio' && file.url && !audioStates[file.id]) {
        const audio = document.createElement('audio');
        audio.preload = 'metadata';
        audio.onloadedmetadata = () => {
          setAudioStates(prev => ({
            ...prev,
            [file.id]: {
              playing: false,
              duration: audio.duration
            }
          }));
        };
        audio.src = file.url;
      }
    });
  }, [files, audioStates]);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAudioPlay = (fileId: string, url: string) => {
    // 这里可以实现音频播放逻辑
    // console.log('Play audio:', fileId, url);
  };

  if (files.length === 0) return null;

  return (
    <AttachmentContainer>
      <AttachmentList>
        {files.map((file) => (
          <AttachmentItem key={file.id} $status={file.status}>
            {getFileIcon(file.type, file)}
            
            <FileInfo>
              <FileName>{file.file.name}</FileName>
              <FileDetails>
                <span>{formatFileSize(file.file.size)}</span>
                <FileTag $color={
                  file.type === 'image' ? '#dcfce7' :
                  file.type === 'audio' ? '#f3e8ff' :
                  '#fef2f2'
                }>
                  {file.type}
                </FileTag>
                
                {file.type === 'audio' && audioStates[file.id] && (
                  <span>{formatDuration(audioStates[file.id].duration)}</span>
                )}
                
                <StatusIndicator $status={file.status}>
                  {file.status === 'uploading' && <LoadingOutlined />}
                  {file.status === 'success' && '✓'}
                  {file.status === 'error' && '✗'}
                  {file.status === 'success' ? '已上传' :
                   file.status === 'error' ? '失败' :
                   file.status === 'uploading' ? '上传中' : '待上传'}
                </StatusIndicator>
              </FileDetails>
              
              {/* 音频播放器 */}
              {file.type === 'audio' && file.url && !compact && (
                <AudioPlayer>
                  <PlayButton
                    icon={<PlayCircleOutlined />}
                    onClick={() => handleAudioPlay(file.id, file.url!)}
                    size="small"
                  />
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary, #8e8ea0)' }}>
                    音频文件 • {audioStates[file.id] ? formatDuration(audioStates[file.id].duration) : '加载中...'}
                  </span>
                </AudioPlayer>
              )}
            </FileInfo>
            
            <RemoveButton
              icon={<DeleteOutlined />}
              onClick={() => onRemove(file.id)}
              title="移除文件"
            />
            
            {/* 上传进度条 */}
            {file.status === 'uploading' && file.progress !== undefined && (
              <ProgressBar $progress={file.progress} />
            )}
          </AttachmentItem>
        ))}
      </AttachmentList>
    </AttachmentContainer>
  );
};

export default ChatFileAttachment; 